import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { date as fmtDate } from "@/lib/format";
import { Button, Input, Modal, PageHeader, Pill, RowActions, Select, Table, Td, Th } from "@/components/ui";
import { popup } from "@/components/popup";

interface Operator {
  id: number;
  telegram_id: number;
  display_name: string | null;
  role: string;
  is_active: boolean;
  created_at: string | null;
}

/** Роль пишем словом, а не «оператор/админ» — иначе у администратора кнопка
 *  «Сделать оператором» читалась как бессмыслица. */
const ROLE_LABEL: Record<string, string> = {
  admin: "все обращения",
  operator: "только свои",
};

function OperatorMenu({
  operator,
  onRole,
  onToggle,
}: {
  operator: Operator;
  onRole: (role: string) => void;
  onToggle: () => void;
}) {
  const isAdmin = operator.role === "admin";

  const item = (
    close: () => void,
    label: string,
    hint: string,
    fn: () => void,
    tone?: "danger",
  ) => (
    <button
      onClick={() => {
        close();
        fn();
      }}
      className={`w-full text-left px-3 py-2 rounded-lg transition-colors hover:bg-white/[0.07] ${
        tone === "danger" ? "text-rose-300 hover:bg-rose-500/10" : "text-zinc-300"
      }`}
    >
      <div className="text-[13px]">{label}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{hint}</div>
    </button>
  );

  return (
    <RowActions>
      {(close) => (
        <>
          {isAdmin
            ? item(close, "Ограничить правами оператора", "видит только свои обращения", () =>
                onRole("operator"),
              )
            : item(close, "Дать полный доступ", "все обращения, передача и закрытие чужих", () =>
                onRole("admin"),
              )}
          <div className="my-1 mx-2 border-t border-white/[0.07]" />
          {operator.is_active
            ? item(close, "Отключить доступ", "не сможет войти в панель и бот оператора", onToggle, "danger")
            : item(close, "Включить доступ", "снова получит доступ к обращениям", onToggle)}
        </>
      )}
    </RowActions>
  );
}

export default function Operators() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tgId, setTgId] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("operator");

  const q = useQuery<Operator[]>({
    queryKey: ["operators"],
    queryFn: () => api("/api/admin/operators"),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["operators"] });

  const create = useMutation({
    mutationFn: () =>
      api<Operator>("/api/admin/operators", {
        method: "POST",
        body: JSON.stringify({
          telegram_id: Number(tgId),
          display_name: name || null,
          role,
        }),
      }),
    onSuccess: () => {
      popup({ tone: "success", title: "Оператор добавлен", body: "Доступ выдан." });
      setOpen(false);
      setTgId("");
      setName("");
      setRole("operator");
      refresh();
    },
    onError: (e) => popup({ tone: "error", title: "Оператор", body: (e as Error).message }),
  });

  const patch = useMutation({
    mutationFn: (v: { id: number; body: Record<string, unknown> }) =>
      api<Operator>(`/api/admin/operators/${v.id}`, {
        method: "PATCH",
        body: JSON.stringify(v.body),
      }),
    onSuccess: refresh,
    onError: (e) => popup({ tone: "error", title: "Оператор", body: (e as Error).message }),
  });

  return (
    <div className="p-4 md:p-8 space-y-4">
      <PageHeader
        title="Операторы поддержки"
        right={<Button variant="primary" onClick={() => setOpen(true)}>Добавить</Button>}
      />

      <Table>
        <thead>
          <tr>
            <Th fit>ID</Th>
            <Th>Оператор</Th>
            <Th>Telegram</Th>
            <Th>Права</Th>
            <Th fit>Статус</Th>
            <Th fit>Добавлен</Th>
            <Th fit align="right">Действия</Th>
          </tr>
        </thead>
        <tbody>
          {(q.data ?? []).map((o) => (
            <tr
              key={o.id}
              className={`border-t border-white/[0.04] ${o.is_active ? "" : "opacity-50"}`}
            >
              <Td fit className="text-zinc-500">{o.id}</Td>
              <Td className="text-zinc-100">{o.display_name || `Оператор #${o.id}`}</Td>
              <Td className="text-zinc-400 tabular-nums">{o.telegram_id}</Td>
              <Td>
                <Pill tone={o.role === "admin" ? "warning" : "neutral"}>
                  {ROLE_LABEL[o.role] ?? o.role}
                </Pill>
              </Td>
              <Td fit>
                <Pill tone={o.is_active ? "success" : "danger"}>
                  {o.is_active ? "активен" : "выключен"}
                </Pill>
              </Td>
              <Td fit className="text-zinc-500 text-[11px] whitespace-nowrap">
                {o.created_at ? fmtDate(o.created_at) : "—"}
              </Td>
              <Td fit align="right">
                <OperatorMenu
                  operator={o}
                  onRole={(role) => patch.mutate({ id: o.id, body: { role } })}
                  onToggle={() => patch.mutate({ id: o.id, body: { is_active: !o.is_active } })}
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {q.data && q.data.length === 0 && (
        <p className="text-sm text-zinc-500">Операторов пока нет — добавьте первого.</p>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Новый оператор">
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Telegram ID</label>
            <Input value={tgId} onChange={(e) => setTgId(e.target.value)} placeholder="123456789" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Имя (для внутреннего лога)</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Иван" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Роль</label>
            <Select
              value={role}
              onChange={setRole}
              options={[
                {
                  value: "operator",
                  label: "Оператор",
                  hint: "видит только свои обращения",
                },
                {
                  value: "admin",
                  label: "Администратор",
                  hint: "все обращения, может передавать и закрывать чужие",
                },
              ]}
            />
          </div>
          <p className="text-xs text-zinc-600">
            Юзер должен сам открыть бота оператора — доступ определяется по Telegram ID.
          </p>
          <Button
            variant="primary"
            disabled={!tgId || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Добавляю…" : "Добавить"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
